// Client for `dashoneNavigation`: the dashboards and workflows the signed-in
// user's navigation offers in one workspace, split by their persona.

import { postWithAuthRetry } from "./onePSfaClient";

interface NavigationSplit {
  in_persona: string[];
  not_in_persona: string[];
}

interface NavigationSfaVariant {
  key: string;
  franchise: string;
  country: string;
  title: string;
}

export interface NavigationResponse {
  persona: string | null;
  // Dashboard card ids, in menu order.
  dashboards: NavigationSplit;
  // Workflow ids.
  workflows: NavigationSplit;
  // Per-franchise audits that take the generic Single Franchise Audit's place.
  sfa_variants: NavigationSfaVariant[];
  user_added: { dashboards: string[]; workflows: string[] };
  // True when the workflow registry failed to load, so no workflow is offered.
  registry_error: boolean;
  // Dashboard card ids withheld because another offered card replaces them,
  // mapped to that card.
  replaced_dashboards?: Record<string, string>;
}

function url(): string {
  const base = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
  if (!base) throw new Error("Firebase functions URL not configured");
  return `${base.replace(/\/+$/, "")}/dashoneNavigation`;
}

export async function fetchNavigation(
  idClient: string,
  sessionPersonaConfig: unknown
): Promise<NavigationResponse> {
  const res = await postWithAuthRetry(url(), {
    id_client: idClient,
    session_persona_config: sessionPersonaConfig ?? undefined,
  });
  if (!res.ok) throw new Error(`dashoneNavigation failed (${res.status})`);
  const body = (await res.json()) as NavigationResponse;
  // Every surface reads both splits unguarded, so a body without them fails the
  // query instead, which the surfaces show as their "Couldn't load" state.
  if (!isSplit(body?.dashboards) || !isSplit(body?.workflows)) {
    throw new Error("dashoneNavigation returned an unreadable answer");
  }
  return body;
}

function isSplit(value: unknown): value is NavigationSplit {
  const split = value as Partial<NavigationSplit> | undefined;
  return Array.isArray(split?.in_persona) && Array.isArray(split?.not_in_persona);
}
